/**
 * Server Info Service
 * Fetches server version and metadata for different database engines.
 */

import { executeQueryV2 } from '../../database-objects/services/queryExecutor';
import { v4 as uuidv4 } from 'uuid';

export type ServerVersionInfo = {
  version: string;
  edition?: string;
  platform?: string;
};

/**
 * Get the SQL query for fetching version info based on engine type
 */
function getVersionQuery(engine: string): string {
  switch (engine) {
    case 'pgsql':
      return 'SELECT version() AS version_info';
    case 'sqlite':
      return 'SELECT sqlite_version() AS version_info';
    case 'mssql':
    case 'mock':
    default:
      return 'SELECT @@VERSION AS version_info';
  }
}

/**
 * Parse the version string based on engine type
 */
function parseVersionInfo(engine: string, versionString: string): ServerVersionInfo {
  switch (engine) {
    case 'pgsql': {
      // PostgreSQL version string format:
      // "PostgreSQL 15.2 on x86_64-pc-linux-gnu, compiled by gcc (Ubuntu 11.3.0-1ubuntu1~22.04) 11.3.0, 64-bit"
      const pgMatch = versionString.match(/^(PostgreSQL\s+[\d.]+)/);
      const platformMatch = versionString.match(/on\s+([^,]+)/);
      return {
        version: pgMatch?.[1] || versionString,
        platform: platformMatch?.[1]?.trim(),
      };
    }
    case 'sqlite': {
      // SQLite just returns version number like "3.40.1"
      return {
        version: `SQLite ${versionString}`,
      };
    }
    case 'mssql':
    case 'mock':
    default: {
      // MSSQL version string format:
      // "Microsoft SQL Server 2022 (RTM-CU16) (KB5048033) - 16.0.4165.4 (X64) \n\tNov  6 2024 19:24:49 \n\tCopyright (C) 2022 Microsoft Corporation\n\tDeveloper Edition (64-bit) on Linux (Ubuntu 22.04.5 LTS) <X64>"
      const lines = versionString.split('\n').map(l => l.trim()).filter(Boolean);
      const firstLine = lines[0] || versionString;

      // Extract edition from the last line
      const editionMatch = versionString.match(/(Developer|Enterprise|Standard|Express|Web|Workgroup|Datacenter)\s+Edition/i);

      // Extract platform from the last line
      const platformMatch = versionString.match(/on\s+(Linux|Windows)[^<]*/i);

      return {
        version: firstLine,
        edition: editionMatch?.[0],
        platform: platformMatch?.[0]?.replace(/^on\s+/i, '').trim(),
      };
    }
  }
}

/**
 * Fetch server version info for a connection
 */
export async function fetchServerVersionInfo(
  connectionId: string,
  engine: string,
  database?: string
): Promise<ServerVersionInfo> {
  const query = getVersionQuery(engine);
  const executionId = uuidv4();

  const result = await executeQueryV2({
    executionId,
    connectionId,
    sql: query,
    database,
  });

  if (!result.success || !result.resultSets?.[0]?.rows?.[0]) {
    throw new Error(result.error?.message || 'Failed to fetch server version');
  }

  const row = result.resultSets[0].rows[0];
  const versionString = row.version_info as string;

  return parseVersionInfo(engine, versionString);
}
